import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import type {
  ProductionDeploymentSummary,
  ProductionReleaseSummary,
  ProductionUpdateEventSummary,
} from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  FilterBar,
  FormField,
  Input,
  Modal,
  Pagination,
  Select,
  SkeletonRows,
  StatusBadge,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type StatusBadgeTone,
} from '@/components/ui';
import {
  createUpdateRelease,
  deleteDraftUpdateRelease,
  getUpdateRelease,
  listUpdateClientsGlobal,
  listUpdateCustomersGlobal,
  listUpdateDeployments,
  listUpdateEventsGlobal,
  listUpdateReleases,
  publishUpdateRelease,
  type UpdateClientListItem,
  type UpdateCustomerListItem,
  updateDeploymentAction,
  uploadUpdateReleaseArtifact,
  useQuery,
  validateUpdateRelease,
} from '@/data';
import { errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/cn';

type Tab = 'releases' | 'deployments' | 'customers' | 'clients' | 'events';

const TABS: { id: Tab; label: string }[] = [
  { id: 'releases', label: 'Releases' },
  { id: 'deployments', label: 'Deployments' },
  { id: 'customers', label: 'Update Customers' },
  { id: 'clients', label: 'Clients' },
  { id: 'events', label: 'Events' },
];

const key = () => crypto.randomUUID();

function statusTone(status: string): StatusBadgeTone {
  if (status === 'published' || status === 'active' || status === 'completed') return 'success';
  if (status === 'draft' || status === 'paused' || status === 'scheduled') return 'warning';
  if (status === 'cancelled' || status === 'failed' || status === 'revoked') return 'danger';
  if (status === 'archived') return 'neutral';
  return 'info';
}

function LifecycleBadge({ status }: { status: string }) {
  return <StatusBadge tone={statusTone(status)} label={status.replaceAll('_', ' ')} />;
}

export function DeploymentCenterPage() {
  const canManage = useCan()('update.manage');
  const [tab, setTab] = useState<Tab>('releases');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Create release modal
  const [createOpen, setCreateOpen] = useState(false);
  const [version, setVersion] = useState('');
  const [channel, setChannel] = useState<'stable' | 'rc' | 'beta'>('stable');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  // Release detail
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  // Deployment confirms
  const [deployAction, setDeployAction] = useState<{ id: string; action: 'pause' | 'resume' | 'cancel' } | null>(null);

  const releases = useQuery(
    () => listUpdateReleases({ page, limit: pageSize, status: statusFilter || undefined }),
    [tab === 'releases' ? page : 0, pageSize, statusFilter, tab],
  );
  const deployments = useQuery(
    () => listUpdateDeployments({ page, limit: pageSize }),
    [tab === 'deployments' ? page : 0, pageSize, tab],
  );
  const customers = useQuery(
    () => listUpdateCustomersGlobal({ limit: pageSize }),
    [tab === 'customers' ? pageSize : 0, tab],
  );
  const clients = useQuery(
    () => listUpdateClientsGlobal({ limit: pageSize }),
    [tab === 'clients' ? pageSize : 0, tab],
  );
  const events = useQuery(
    () => listUpdateEventsGlobal({ page, limit: pageSize }),
    [tab === 'events' ? page : 0, pageSize, tab],
  );

  const detail = useQuery(
    () => (selectedId ? getUpdateRelease(selectedId) : Promise.resolve(null)),
    [selectedId],
  );

  const activeQuery = tab === 'releases' ? releases : tab === 'deployments' ? deployments : tab === 'customers' ? customers : tab === 'clients' ? clients : events;

  const switchTab = (next: Tab) => {
    setTab(next);
    setPage(1);
    setError(null);
    setNotice(null);
    setSelectedId(null);
  };

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(ok);
      await activeQuery.reload();
      if (selectedId) await detail.reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const createRelease = async () => {
    if (!version.trim()) return;
    await run(
      () =>
        createUpdateRelease({
          version: version.trim(),
          channel,
          releaseNotes: notes,
          idempotencyKey: key(),
        }),
      'Draft release created.',
    );
    setCreateOpen(false);
    setVersion('');
    setNotes('');
  };

  const selectedRelease = useMemo(() => {
    if (!selectedId || !releases.data) return null;
    return releases.data.items.find((r) => r.id === selectedId) ?? null;
  }, [selectedId, releases.data]);

  return (
    <>
      <PageHeader
        title="Deployment Center"
        subtitle="Releases, deployments, update customers, clients, and events"
        actions={
          canManage && tab === 'releases' ? (
            <Button
              variant="primary"
              onClick={() => {
                setCreateOpen(true);
                setError(null);
              }}
            >
              Create release
            </Button>
          ) : undefined
        }
      />

      <div className="card overflow-hidden">
        <div
          className="flex flex-wrap gap-0 border-b border-line bg-surface-muted/60 px-2 pt-2"
          role="tablist"
          aria-label="Deployment Center sections"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => switchTab(t.id)}
              className={cn(
                '-mb-px border-b-2 px-3.5 py-2.5 text-sm transition-colors focus-visible:shadow-focus focus-visible:outline-none',
                tab === t.id
                  ? 'border-primary bg-surface font-semibold text-ink'
                  : 'border-transparent text-ink-secondary hover:text-ink',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {(error || notice) && (
          <div className="space-y-2 border-b border-line px-4 py-3">
            {error && <p role="alert" className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>}
            {notice && <p role="status" className="rounded-md border border-success/30 bg-success-soft px-3 py-2 text-[13px] text-success">{notice}</p>}
          </div>
        )}

        {tab === 'releases' && (
          <div className="grid gap-0 lg:grid-cols-[1fr_minmax(280px,360px)]">
            <div>
              <FilterBar
                dirty={!!statusFilter}
                onReset={() => {
                  setStatusFilter('');
                  setPage(1);
                }}
                trailing={
                  <Button variant="ghost" size="sm" onClick={releases.reload} disabled={releases.loading || releases.refreshing}>
                    Refresh
                  </Button>
                }
              >
                <Select
                  aria-label="Release status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  options={[
                    { value: '', label: 'All statuses' },
                    { value: 'draft', label: 'Draft' },
                    { value: 'published', label: 'Published' },
                    { value: 'archived', label: 'Archived' },
                  ]}
                  className="w-44"
                />
              </FilterBar>
              {releases.error ? (
                <ErrorState message={releases.error.message} action={<Button variant="secondary" onClick={releases.reload}>Try again</Button>} />
              ) : (
                <ReleasesTable
                  items={releases.data?.items ?? []}
                  loading={releases.loading}
                  refreshing={releases.refreshing}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                />
              )}
              {releases.data && (
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  total={releases.data.total ?? releases.data.items.length}
                  onPageChange={setPage}
                  onPageSizeChange={(n) => {
                    setPageSize(n);
                    setPage(1);
                  }}
                />
              )}
            </div>
            <aside className="border-t border-line lg:border-l lg:border-t-0">
              {!selectedId ? (
                <EmptyState
                  className="py-12"
                  icon={<Download size={18} aria-hidden="true" />}
                  title="Select a release"
                  description="Upload artifacts, validate, publish, or delete drafts from the detail panel."
                />
              ) : detail.loading ? (
                <p className="p-4 text-[13px] text-ink-secondary">Loading release…</p>
              ) : detail.error ? (
                <ErrorState message={detail.error.message} />
              ) : (
                <ReleaseDetailPanel
                  release={(detail.data?.release as ProductionReleaseSummary) ?? selectedRelease}
                  artifacts={detail.data?.artifacts ?? []}
                  canManage={canManage}
                  busy={busy}
                  uploadFile={uploadFile}
                  onUploadFile={setUploadFile}
                  onUpload={() => {
                    if (!selectedId || !uploadFile) return;
                    void run(
                      () => uploadUpdateReleaseArtifact(selectedId, uploadFile, { idempotencyKey: key(), artifactType: 'installer' }),
                      'Artifact uploaded.',
                    ).then(() => setUploadFile(null));
                  }}
                  onValidate={() => selectedId && void run(() => validateUpdateRelease(selectedId, { idempotencyKey: key() }), 'Validation complete.')}
                  onPublish={() => setConfirmPublish(true)}
                  onDelete={() => setConfirmDelete(true)}
                />
              )}
            </aside>
          </div>
        )}

        {tab === 'deployments' && (
          <>
            {deployments.error ? (
              <ErrorState message={deployments.error.message} action={<Button variant="secondary" onClick={deployments.reload}>Try again</Button>} />
            ) : (
              <DeploymentsTable
                items={deployments.data?.items ?? []}
                loading={deployments.loading}
                refreshing={deployments.refreshing}
                canManage={canManage}
                busy={busy}
                onAction={(id, action) => setDeployAction({ id, action })}
              />
            )}
            {deployments.data && (
              <Pagination
                page={page}
                pageSize={pageSize}
                total={deployments.data.total ?? deployments.data.items.length}
                onPageChange={setPage}
                onPageSizeChange={(n) => {
                  setPageSize(n);
                  setPage(1);
                }}
              />
            )}
          </>
        )}

        {tab === 'customers' && (
          <>
            {customers.error ? (
              <ErrorState message={customers.error.message} action={<Button variant="secondary" onClick={customers.reload}>Try again</Button>} />
            ) : (
              <CustomersTable items={customers.data?.items ?? []} loading={customers.loading} refreshing={customers.refreshing} />
            )}
          </>
        )}

        {tab === 'clients' && (
          <>
            {clients.error ? (
              <ErrorState message={clients.error.message} action={<Button variant="secondary" onClick={clients.reload}>Try again</Button>} />
            ) : (
              <ClientsTable items={clients.data?.items ?? []} loading={clients.loading} refreshing={clients.refreshing} />
            )}
          </>
        )}

        {tab === 'events' && (
          <>
            {events.error ? (
              <ErrorState message={events.error.message} action={<Button variant="secondary" onClick={events.reload}>Try again</Button>} />
            ) : (
              <EventsTable items={events.data?.items ?? []} loading={events.loading} refreshing={events.refreshing} />
            )}
            {events.data && (
              <Pagination
                page={page}
                pageSize={pageSize}
                total={events.data.total ?? events.data.items.length}
                onPageChange={setPage}
                onPageSizeChange={(n) => {
                  setPageSize(n);
                  setPage(1);
                }}
              />
            )}
          </>
        )}
      </div>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create draft release"
        description="Metadata only — upload the installer afterward."
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void createRelease()} disabled={busy || !version.trim()}>
              {busy ? 'Creating…' : 'Create draft'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Version" htmlFor="rel-version" required>
            <Input id="rel-version" placeholder="1.2.3" value={version} onChange={(e) => setVersion(e.target.value)} />
          </FormField>
          <FormField label="Channel" htmlFor="rel-channel">
            <Select
              id="rel-channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value as 'stable' | 'rc' | 'beta')}
              options={[
                { value: 'stable', label: 'Stable' },
                { value: 'rc', label: 'Release candidate' },
                { value: 'beta', label: 'Beta' },
              ]}
            />
          </FormField>
          <FormField label="Release notes" htmlFor="rel-notes">
            <Input id="rel-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </FormField>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmPublish}
        onCancel={() => setConfirmPublish(false)}
        title="Publish release"
        message="Published releases become available to updater clients on the selected channel. This cannot be undone from Central Admin."
        confirmLabel="Publish"
        tone="primary"
        loading={busy}
        onConfirm={() => {
          if (!selectedId) return;
          void run(() => publishUpdateRelease(selectedId, { idempotencyKey: key() }), 'Release published.').then(() =>
            setConfirmPublish(false),
          );
        }}
      />

      <ConfirmDialog
        open={confirmDelete}
        onCancel={() => setConfirmDelete(false)}
        title="Delete draft release"
        message="Only draft releases can be deleted. This removes the draft and its artifacts from Production."
        confirmLabel="Delete draft"
        tone="danger"
        loading={busy}
        onConfirm={() => {
          if (!selectedId) return;
          void run(() => deleteDraftUpdateRelease(selectedId, { idempotencyKey: key() }), 'Draft deleted.').then(() => {
            setConfirmDelete(false);
            setSelectedId(null);
          });
        }}
      />

      <ConfirmDialog
        open={Boolean(deployAction)}
        onCancel={() => setDeployAction(null)}
        title={
          deployAction?.action === 'pause'
            ? 'Pause deployment'
            : deployAction?.action === 'resume'
              ? 'Resume deployment'
              : 'Cancel deployment'
        }
        message="This changes live updater targeting in Production. Confirm only if you intend this change."
        confirmLabel={deployAction?.action === 'cancel' ? 'Cancel deployment' : 'Confirm'}
        tone={deployAction?.action === 'cancel' ? 'danger' : 'primary'}
        loading={busy}
        onConfirm={() => {
          if (!deployAction) return;
          void run(
            () => updateDeploymentAction(deployAction.id, deployAction.action, { idempotencyKey: key() }),
            `Deployment ${deployAction.action} completed.`,
          ).then(() => setDeployAction(null));
        }}
      />
    </>
  );
}

function ReleasesTable({
  items,
  loading,
  refreshing,
  selectedId,
  onSelect,
}: {
  items: ProductionReleaseSummary[];
  loading: boolean;
  refreshing: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <TableContainer label="Releases">
      <Table className="min-w-[640px]" aria-busy={loading || refreshing}>
        <THead>
          <tr>
            <TH className="pl-5">Version</TH>
            <TH>Channel</TH>
            <TH>Status</TH>
            <TH className="pr-5">Product</TH>
          </tr>
        </THead>
        <TBody className={refreshing ? 'opacity-60' : undefined}>
          {loading ? (
            <SkeletonRows rows={8} columns={4} />
          ) : items.length === 0 ? (
            <tr>
              <TD colSpan={4} className="py-10 text-center text-ink-secondary">
                No releases
              </TD>
            </tr>
          ) : (
            items.map((r) => (
              <TR
                key={r.id}
                interactive
                className={selectedId === r.id ? 'bg-primary-soft/50' : undefined}
                onClick={() => onSelect(r.id)}
              >
                <TD className="h-11 pl-5 font-medium">{r.version}</TD>
                <TD className="h-11">{r.channel}</TD>
                <TD className="h-11">
                  <LifecycleBadge status={r.status} />
                </TD>
                <TD className="h-11 pr-5 text-ink-secondary">{r.productKey}</TD>
              </TR>
            ))
          )}
        </TBody>
      </Table>
    </TableContainer>
  );
}

function ReleaseDetailPanel({
  release,
  artifacts,
  canManage,
  busy,
  uploadFile,
  onUploadFile,
  onUpload,
  onValidate,
  onPublish,
  onDelete,
}: {
  release: ProductionReleaseSummary | null;
  artifacts: Array<Record<string, unknown>>;
  canManage: boolean;
  busy: boolean;
  uploadFile: File | null;
  onUploadFile: (f: File | null) => void;
  onUpload: () => void;
  onValidate: () => void;
  onPublish: () => void;
  onDelete: () => void;
}) {
  if (!release) return null;
  const isDraft = release.status === 'draft';
  return (
    <div className="space-y-4 p-4">
      <div>
        <h2 className="flex flex-wrap items-center gap-2 text-base font-semibold text-ink">
          {release.version}
          <LifecycleBadge status={release.status} />
        </h2>
        <p className="mt-1 text-[13px] text-ink-secondary">
          {release.channel} · {release.productKey}
        </p>
      </div>
      {artifacts.length > 0 && (
        <ul className="divide-y divide-line rounded-md border border-line text-[13px]">
          {artifacts.map((a, i) => (
            <li key={String(a.id ?? i)} className="px-3 py-2">
              {String(a.filename ?? a.artifactType ?? 'artifact')}
              {typeof a.sizeBytes === 'number' ? ` · ${a.sizeBytes} bytes` : ''}
            </li>
          ))}
        </ul>
      )}
      {canManage && isDraft && (
        <div className="space-y-3">
          <FormField label="Installer file" htmlFor="artifact-file">
            <input
              id="artifact-file"
              type="file"
              className="block w-full text-[13px] text-ink-secondary"
              onChange={(e) => onUploadFile(e.target.files?.[0] ?? null)}
            />
          </FormField>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" disabled={busy || !uploadFile} onClick={onUpload}>
              Upload
            </Button>
            <Button variant="secondary" size="sm" disabled={busy} onClick={onValidate}>
              Validate
            </Button>
            <Button variant="primary" size="sm" disabled={busy} onClick={onPublish}>
              Publish
            </Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={onDelete}>
              Delete draft
            </Button>
          </div>
        </div>
      )}
      {!canManage && <p className="text-[13px] text-ink-secondary">View only — your role cannot manage releases.</p>}
    </div>
  );
}

function DeploymentsTable({
  items,
  loading,
  refreshing,
  canManage,
  busy,
  onAction,
}: {
  items: ProductionDeploymentSummary[];
  loading: boolean;
  refreshing: boolean;
  canManage: boolean;
  busy: boolean;
  onAction: (id: string, action: 'pause' | 'resume' | 'cancel') => void;
}) {
  return (
    <TableContainer label="Deployments">
      <Table className="min-w-[720px]" aria-busy={loading || refreshing}>
        <THead>
          <tr>
            <TH className="pl-5">Name</TH>
            <TH>Status</TH>
            <TH>Target</TH>
            <TH>Release</TH>
            <TH className="pr-5">Actions</TH>
          </tr>
        </THead>
        <TBody className={refreshing ? 'opacity-60' : undefined}>
          {loading ? (
            <SkeletonRows rows={8} columns={5} />
          ) : items.length === 0 ? (
            <tr>
              <TD colSpan={5} className="py-10 text-center text-ink-secondary">
                No deployments
              </TD>
            </tr>
          ) : (
            items.map((d) => (
              <TR key={d.id}>
                <TD className="h-11 pl-5 font-medium">{d.name}</TD>
                <TD className="h-11">
                  <LifecycleBadge status={d.status} />
                </TD>
                <TD className="h-11 text-ink-secondary">{d.targetType}</TD>
                <TD className="h-11 text-ink-secondary">{d.releaseVersion ?? d.releaseId.slice(0, 8)}</TD>
                <TD className="h-11 pr-5">
                  {canManage && (
                    <div className="flex flex-wrap gap-1">
                      {(d.status === 'active' || d.status === 'running') && (
                        <Button variant="ghost" size="sm" disabled={busy} onClick={() => onAction(d.id, 'pause')}>
                          Pause
                        </Button>
                      )}
                      {d.status === 'paused' && (
                        <Button variant="ghost" size="sm" disabled={busy} onClick={() => onAction(d.id, 'resume')}>
                          Resume
                        </Button>
                      )}
                      {d.status !== 'cancelled' && d.status !== 'completed' && (
                        <Button variant="ghost" size="sm" disabled={busy} onClick={() => onAction(d.id, 'cancel')}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  )}
                </TD>
              </TR>
            ))
          )}
        </TBody>
      </Table>
    </TableContainer>
  );
}

function CustomersTable({
  items,
  loading,
  refreshing,
}: {
  items: UpdateCustomerListItem[];
  loading: boolean;
  refreshing: boolean;
}) {
  return (
    <TableContainer label="Update customers">
      <Table className="min-w-[720px]" aria-busy={loading || refreshing}>
        <THead>
          <tr>
            <TH className="pl-5">Pharmacy</TH>
            <TH>Status</TH>
            <TH>Channel</TH>
            <TH>Devices</TH>
            <TH className="pr-5">Updated</TH>
          </tr>
        </THead>
        <TBody className={refreshing ? 'opacity-60' : undefined}>
          {loading ? (
            <SkeletonRows rows={8} columns={5} />
          ) : items.length === 0 ? (
            <tr>
              <TD colSpan={5} className="py-10 text-center text-ink-secondary">
                No update customers
              </TD>
            </tr>
          ) : (
            items.map((c) => (
              <TR key={c.id}>
                <TD className="h-11 pl-5">
                  <div className="font-medium">{c.pharmacyName}</div>
                  <div className="text-[12px] text-ink-muted">{c.id}</div>
                </TD>
                <TD className="h-11">
                  <LifecycleBadge status={c.status} />
                </TD>
                <TD className="h-11">{c.assignedChannel}</TD>
                <TD className="h-11">
                  {c.activeDeviceCount}/{c.deviceCount}
                </TD>
                <TD className="h-11 pr-5 text-ink-secondary">{formatDateTime(c.updatedAt)}</TD>
              </TR>
            ))
          )}
        </TBody>
      </Table>
    </TableContainer>
  );
}

function ClientsTable({
  items,
  loading,
  refreshing,
}: {
  items: UpdateClientListItem[];
  loading: boolean;
  refreshing: boolean;
}) {
  return (
    <TableContainer label="Update clients">
      <Table className="min-w-[720px]" aria-busy={loading || refreshing}>
        <THead>
          <tr>
            <TH className="pl-5">Device</TH>
            <TH>Version</TH>
            <TH>Channel</TH>
            <TH>Last status</TH>
            <TH className="pr-5">Last seen</TH>
          </tr>
        </THead>
        <TBody className={refreshing ? 'opacity-60' : undefined}>
          {loading ? (
            <SkeletonRows rows={8} columns={5} />
          ) : items.length === 0 ? (
            <tr>
              <TD colSpan={5} className="py-10 text-center text-ink-secondary">
                No clients
              </TD>
            </tr>
          ) : (
            items.map((c) => (
              <TR key={c.id}>
                <TD className="h-11 pl-5 font-medium">{c.deviceId}</TD>
                <TD className="h-11">{c.currentVersion}</TD>
                <TD className="h-11">{c.releaseChannel}</TD>
                <TD className="h-11 text-ink-secondary">
                  {c.lastUpdateStatus ? <LifecycleBadge status={c.lastUpdateStatus} /> : '—'}
                </TD>
                <TD className="h-11 pr-5 text-ink-secondary">{c.lastSeenAt ? formatDateTime(c.lastSeenAt) : '—'}</TD>
              </TR>
            ))
          )}
        </TBody>
      </Table>
    </TableContainer>
  );
}

function EventsTable({
  items,
  loading,
  refreshing,
}: {
  items: ProductionUpdateEventSummary[];
  loading: boolean;
  refreshing: boolean;
}) {
  return (
    <TableContainer label="Update events">
      <Table className="min-w-[720px]" aria-busy={loading || refreshing}>
        <THead>
          <tr>
            <TH className="pl-5">Event</TH>
            <TH>Device</TH>
            <TH>Versions</TH>
            <TH>Failure</TH>
            <TH className="pr-5">Occurred</TH>
          </tr>
        </THead>
        <TBody className={refreshing ? 'opacity-60' : undefined}>
          {loading ? (
            <SkeletonRows rows={8} columns={5} />
          ) : items.length === 0 ? (
            <tr>
              <TD colSpan={5} className="py-10 text-center text-ink-secondary">
                No events
              </TD>
            </tr>
          ) : (
            items.map((e) => (
              <TR key={e.id}>
                <TD className="h-11 pl-5 font-medium">{e.eventType}</TD>
                <TD className="h-11 text-ink-secondary">{e.deviceId ?? '—'}</TD>
                <TD className="h-11 text-ink-secondary">
                  {e.currentVersion ?? '—'}
                  {e.targetVersion ? ` → ${e.targetVersion}` : ''}
                </TD>
                <TD className="h-11 text-ink-secondary">{e.failureCode ?? '—'}</TD>
                <TD className="h-11 pr-5 text-ink-secondary">{e.occurredAt ? formatDateTime(e.occurredAt) : '—'}</TD>
              </TR>
            ))
          )}
        </TBody>
      </Table>
    </TableContainer>
  );
}
